// =============================================================
// PERPETUUM — i18n scaffold (LV · RU · EN).
//
// Ported from the prototype dictionaries (scripts/perpetuum-i18n.js +
// perpetuum-mobile-lib.jsx). Binding constraint (PROJECT.md): every screen
// supports LV/RU/EN with live switching; EN is the fallback, then the key
// itself. Dot-path keys (e.g. "wo.loto") resolve into the nested dictionary.
//
// This is a SCAFFOLD — a representative seed of keys, not the full catalogue
// (the complete strings land with the UI port slice). The `t()` contract is
// final; new keys slot into the dictionaries without touching the helper.
// =============================================================

import type { Lang } from "../types/index.js";

/** Supported languages, in display order. */
export const LANGS: readonly Lang[] = ["lv", "ru", "en"] as const;

/** The fallback language when a key is missing in the active language. */
export const FALLBACK_LANG: Lang = "en";

/** A (possibly nested) dictionary of translation strings. */
export interface Dict {
  [key: string]: string | Dict;
}

/** Seed dictionary — common UI + the safety-critical WO/LOTO/tolerance strings. */
export const dictionaries: Record<Lang, Dict> = {
  en: {
    common: {
      add: "Add",
      save: "Save",
      cancel: "Cancel",
      close: "Close",
      export: "Export",
      search: "Search assets, work orders…",
      signOut: "Sign out",
    },
    nav: {
      dashboard: "Reports & dashboard",
      tasks: "Tasks register",
      tree: "Location & device tree",
      inventory: "Spares inventory",
      downtimes: "Downtime register",
      measurements: "Measurement templates",
    },
    wo: {
      loto: "LOTO safety gate",
      lotoMsg:
        "Lock out & tag out all energy sources before any technical step. The checklist stays locked until LOTO is confirmed.",
      lotoConfirm: "Confirm lockout/tagout",
      lotoDone: "LOTO confirmed",
      lotoLocked: "Locked until LOTO confirmed",
      start: "Start task",
      pause: "Pause",
      resume: "Resume",
      stop: "Stop",
      checklist: "Checklist",
      measurements: "Measurements",
      verifiedNfc: "Verified by NFC",
      verifiedQr: "Verified by QR",
    },
    tolerance: { warn: "Outside warning band", alarm: "Outside alarm band" },
  },
  lv: {
    common: {
      add: "Pievienot",
      save: "Saglabāt",
      cancel: "Atcelt",
      close: "Aizvērt",
      export: "Eksportēt",
      search: "Meklēt iekārtas, darbus…",
      signOut: "Iziet",
    },
    nav: {
      dashboard: "Pārskati un panelis",
      tasks: "Uzdevumu reģistrs",
      tree: "Iekārtu koks",
      inventory: "Rezerves daļas",
      downtimes: "Dīkstāvju reģistrs",
      measurements: "Mērījumu veidnes",
    },
    wo: {
      loto: "LOTO drošības solis",
      lotoMsg:
        "Pirms jebkura tehniskā soļa bloķējiet un marķējiet visus enerģijas avotus. Kontrolsaraksts ir slēgts, līdz LOTO ir apstiprināts.",
      lotoConfirm: "Apstiprināt bloķēšanu (LOTO)",
      lotoDone: "LOTO apstiprināts",
      lotoLocked: "Slēgts līdz LOTO apstiprināšanai",
      start: "Sākt uzdevumu",
      pause: "Pauze",
      resume: "Turpināt",
      stop: "Apturēt",
      checklist: "Kontrolsaraksts",
      measurements: "Mērījumi",
      verifiedNfc: "Verificēts ar NFC",
      verifiedQr: "Verificēts ar QR",
    },
    tolerance: {
      warn: "Ārpus brīdinājuma joslas",
      alarm: "Ārpus trauksmes joslas",
    },
  },
  ru: {
    common: {
      add: "Добавить",
      save: "Сохранить",
      cancel: "Отмена",
      close: "Закрыть",
      export: "Экспорт",
      search: "Поиск устройств, нарядов…",
      signOut: "Выйти",
    },
    nav: {
      dashboard: "Отчёты и панель",
      tasks: "Реестр задач",
      tree: "Дерево устройств",
      inventory: "Склад запчастей",
      downtimes: "Реестр простоев",
      measurements: "Шаблоны измерений",
    },
    wo: {
      loto: "Блокировка LOTO",
      lotoMsg:
        "Заблокируйте и пометьте все источники энергии до любого технического шага. Чек-лист закрыт, пока LOTO не подтверждён.",
      lotoConfirm: "Подтвердить блокировку (LOTO)",
      lotoDone: "LOTO подтверждён",
      lotoLocked: "Закрыто до подтверждения LOTO",
      start: "Начать задачу",
      pause: "Пауза",
      resume: "Продолжить",
      stop: "Остановить",
      checklist: "Чек-лист",
      measurements: "Измерения",
      verifiedNfc: "Проверено через NFC",
      verifiedQr: "Проверено через QR",
    },
    tolerance: {
      warn: "Вне зоны предупреждения",
      alarm: "Вне зоны тревоги",
    },
  },
};

/** Resolve a dot-path key against one dictionary; returns undefined if missing. */
function resolve(dict: Dict, key: string): string | undefined {
  const parts = key.split(".");
  let node: string | Dict | undefined = dict;
  for (const part of parts) {
    if (typeof node !== "object" || node === null) return undefined;
    node = node[part];
  }
  return typeof node === "string" ? node : undefined;
}

/** Variables for `{name}`-style interpolation. */
export type TVars = Record<string, string | number>;

/** Interpolate `{var}` placeholders in a resolved string. */
function interpolate(text: string, vars?: TVars): string {
  if (!vars) return text;
  return text.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in vars ? String(vars[name]) : whole,
  );
}

/**
 * Translate a dot-path key into the active language.
 * Falls back to EN, then to the key itself — never throws, never returns blank.
 */
export function t(lang: Lang, key: string, vars?: TVars): string {
  const hit =
    resolve(dictionaries[lang] ?? {}, key) ??
    resolve(dictionaries[FALLBACK_LANG], key) ??
    key;
  return interpolate(hit, vars);
}

/** Bind a language once and return a `t()` curried to it (ergonomic in UIs). */
export function makeT(lang: Lang): (key: string, vars?: TVars) => string {
  return (key, vars) => t(lang, key, vars);
}
